import { ArrowDownTrayIcon, PencilSquareIcon, PlusIcon, PrinterIcon, QrCodeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Checkbox, ConfirmDialog, LinkButton, notify, RowActions, Skeleton, SkeletonRegion, StateBlock } from '../../../components/ui';
import { useApiError } from '../../../hooks/useApiError';
import api from '../../../services/api';
import type { Table } from '../../../types/Table';
import type { LocationRecord } from '../apiTypes';
import { cardLabel, pngCard } from '../printText';
import { codeUrl, downloadPng, downloadSvg, fileName } from '../qr';
import { useRequest } from '../useRequest';
import { AddTablesDialog } from './AddTablesDialog';
import { QrView } from './QrView';
import { TableEditDialog } from './TableEditDialog';

const printHref = (locationId: string, codes: string[] | 'all') => `/locations/${locationId}/qr/print?tables=${codes === 'all' ? 'all' : codes.join(',')}`;

export const TableCodesPanel = ({ location }: { location: LocationRecord }) => {
    const { id: locationId, name: locationName } = location;
    const { t } = useTranslation('locations');
    const apiError = useApiError();
    const { data, state, retry, reload } = useRequest(`tables:${locationId}`, () => api.get<Table[]>(`/locations/${locationId}/tables`).then((r) => r.data));
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState<Table | null>(null);
    const [deleting, setDeleting] = useState<Table | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [selected, setSelected] = useState<string[]>([]);

    const tables = data ?? [];
    const chosen = tables.filter((x) => selected.includes(x.id));
    const allSelected = tables.length > 0 && chosen.length === tables.length;

    const toggle = (id: string, on: boolean) => setSelected((s) => (on ? [...s, id] : s.filter((x) => x !== id)));

    const download = async (table: Table, kind: 'png' | 'svg') => {
        const url = codeUrl(locationId, table.code);
        const part = table.tableNumber ? `table-${table.tableNumber}` : `code-${table.code}`;
        try {
            if (kind === 'png') await downloadPng(url, fileName(locationName, 'png', part), pngCard(location, (lang) => (table.tableNumber ? cardLabel(table.tableNumber, lang) : undefined)));
            else await downloadSvg(url, fileName(locationName, 'svg', part));
        } catch {
            notify.error(t('qr.downloadFailed'));
        }
    };

    const remove = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await api.delete(`/tables/${deleting.id}`);
            notify.success(t('tables.deleted', { name: deleting.tableNumber ?? deleting.code }));
            setSelected((s) => s.filter((x) => x !== deleting.id));
            setDeleting(null);
            reload();
        } catch (e) {
            notify.error(apiError(e));
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <section aria-labelledby="tables-heading">
        <Card padding="none">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 sm:px-6">
                <div>
                    <h2 id="tables-heading" className="text-title text-strong">
                        {t('tables.title')}
                    </h2>
                    <p className="text-small text-muted">{t('tables.body')}</p>
                    <p className="mt-1 text-small text-muted">{t('tables.legacyHint')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {chosen.length > 0 && (
                        <LinkButton
                            to={printHref(
                                locationId,
                                chosen.map((x) => x.code)
                            )}
                            variant="outline"
                            icon={<PrinterIcon className="size-5" aria-hidden="true" />}
                        >
                            {t('tables.printSelected', { count: chosen.length })}
                        </LinkButton>
                    )}
                    {tables.length > 0 && (
                        <LinkButton to={printHref(locationId, 'all')} variant="outline" icon={<PrinterIcon className="size-5" aria-hidden="true" />}>
                            {t('tables.printAll')}
                        </LinkButton>
                    )}
                    <Button variant="primary" onClick={() => setAdding(true)} icon={<PlusIcon className="size-5" aria-hidden="true" />}>
                        {t('tables.add')}
                    </Button>
                </div>
            </div>

            {state === 'error' ? (
                <StateBlock
                    variant="error"
                    title={t('tables.loadFailed')}
                    body={t('common:ui.checkConnection')}
                    action={<Button onClick={retry}>{t('common:ui.retry')}</Button>}
                />
            ) : state === 'loading' || !data ? (
                <SkeletonRegion className="flex flex-col gap-3 p-4">
                    {Array.from({ length: 4 }, (_, i) => (
                        <Skeleton key={i} className="h-12" />
                    ))}
                </SkeletonRegion>
            ) : tables.length === 0 ? (
                <StateBlock
                    variant="empty"
                    icon={QrCodeIcon}
                    title={t('tables.emptyTitle')}
                    body={t('tables.emptyBody')}
                    action={
                        <Button variant="primary" onClick={() => setAdding(true)}>
                            {t('tables.add')}
                        </Button>
                    }
                />
            ) : (
                <>
                    <div className="border-t border-hairline px-4 sm:px-6">
                        <Checkbox
                            checked={allSelected}
                            onChange={(on) => setSelected(on ? tables.map((x) => x.id) : [])}
                            label={chosen.length > 0 ? t('tables.selected', { count: chosen.length }) : t('tables.selectAll')}
                        />
                    </div>
                    <ul>
                        {tables.map((table) => (
                            <li key={table.id} className="flex items-center gap-3 border-t border-hairline px-4 py-3 sm:gap-4 sm:px-6">
                                <Checkbox
                                    checked={selected.includes(table.id)}
                                    onChange={(on) => toggle(table.id, on)}
                                    label={<span className="sr-only">{t('tables.select', { name: table.tableNumber ?? table.code })}</span>}
                                />
                                <QrView
                                    url={codeUrl(locationId, table.code)}
                                    size={192}
                                    label={table.tableNumber ? t('qr.tableAlt', { label: table.tableNumber }) : t('qr.codeAlt', { code: table.code })}
                                    className="size-16 shrink-0 rounded-lg shadow-hairline sm:size-12"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-heading text-strong">{table.tableNumber ?? t('tables.noLabel')}</p>
                                    <p className="truncate text-small text-muted">
                                        <span className="sr-only">{t('tables.codeId')}: </span>
                                        <span className="font-mono">{table.code}</span>
                                    </p>
                                    {table.description && <p className="truncate text-small text-muted">{table.description}</p>}
                                </div>
                                <LinkButton to={printHref(locationId, [table.code])} variant="outline" size="sm" className="hidden sm:inline-flex">
                                    {t('qr.print')}
                                    <span className="sr-only"> {table.tableNumber ?? table.code}</span>
                                </LinkButton>
                                <RowActions
                                    label={table.tableNumber ?? table.code}
                                    actions={[
                                        {
                                            id: 'print',
                                            label: t('qr.print'),
                                            icon: PrinterIcon,
                                            to: printHref(locationId, [table.code]),
                                        },
                                        {
                                            id: 'png',
                                            label: t('qr.png'),
                                            icon: ArrowDownTrayIcon,
                                            onSelect: () => void download(table, 'png'),
                                        },
                                        {
                                            id: 'svg',
                                            label: t('qr.svg'),
                                            icon: ArrowDownTrayIcon,
                                            onSelect: () => void download(table, 'svg'),
                                        },
                                        {
                                            id: 'rename',
                                            label: t('tables.edit'),
                                            icon: PencilSquareIcon,
                                            onSelect: () => setEditing(table),
                                            separated: true,
                                        },
                                        {
                                            id: 'delete',
                                            label: t('common:ui.delete'),
                                            icon: TrashIcon,
                                            tone: 'danger',
                                            onSelect: () => setDeleting(table),
                                        },
                                    ]}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <AddTablesDialog open={adding} onClose={() => setAdding(false)} locationId={locationId} onAdded={reload} />
            <TableEditDialog table={editing} onClose={() => setEditing(null)} onSaved={reload} />
            <ConfirmDialog
                open={deleting !== null}
                onClose={() => setDeleting(null)}
                onConfirm={remove}
                busy={deleteBusy}
                title={deleting?.tableNumber ? t('tables.deleteTitle', { name: deleting.tableNumber }) : t('tables.deleteTitleCode', { code: deleting?.code ?? '' })}
            >
                {t('tables.deleteBody')}
            </ConfirmDialog>
        </Card>
        </section>
    );
};
